<template>
  <ui-menu-base-trigger :disabled :expanded class="overflow-hidden" @focus="handleFocus">
    <template #default="{ openMenu }">
      <component
        :is="theme === UiMenuTriggerTheme.BlueMultiButton ? 'div' : 'button'"
        ref="buttonRef"
        v-tippy="{ content: tooltipContent }"
        :type="theme === UiMenuTriggerTheme.BlueMultiButton ? undefined : 'button'"
        class="flex cursor-pointer items-center gap-2 truncate rounded-md duration-200 ease-out"
        :class="[
          active ? activeClassesMap[theme] : themeClassesMap[theme],
          buttonExpandedColorClassesMap[theme],
          sizeClassesMap[size],
          fullWidth && 'w-full',
          disabled && 'bg-gray-20 hover:bg-gray-20 text-gray-60 cursor-not-allowed border-0',
          error && 'bg-red-10 border-red-40 hover:bg-red-10 focus:bg-red-10 border',
        ]"
        tabindex="0"
        :disabled
        :data-testid
        @click="openMenu"
        @keydown.enter="openMenu"
      >
        <span class="flex shrink-0 duration-200 ease-out empty:hidden">
          <slot name="icon" />
        </span>
        <span
          class="whitespace-nowrap text-xs font-medium duration-200 ease-out empty:hidden"
          :class="labelSlotMap[theme]"
        >
          <slot name="label" />
        </span>
        <span
          class="flex shrink-0 text-xs font-medium empty:hidden"
          :class="
            disableConditions
              ? disabledSlotsMap[theme]
              : active
                ? activeHoverableSlotsMap[theme]
                : hoverableSlotsMap[theme]
          "
        >
          <slot name="condition" />
        </span>
        <span
          class="flex truncate text-xs font-medium empty:hidden"
          :class="active ? activeHoverableSlotsMap[theme] : hoverableSlotsMap[theme]"
        >
          <slot />
        </span>
        <div class="flex gap-1 empty:hidden">
          <div
            v-if="canRemove"
            v-tippy="{ content: removeTooltipContent }"
            data-testid="filter-trigger-remove-button"
            class="ml-auto flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full"
            :class="iconClassesMap[theme]"
            role="button"
            tabindex="0"
            @click="emit('remove')"
            @keyup.enter.exact="emit('remove')"
            @keyup.space.exact="emit('remove')"
          >
            <x-icon class="icon-s" />
          </div>
          <span
            v-if="canAdd"
            role="button"
            tabindex="0"
            class="bg-active-secondary flex h-4 w-4 cursor-pointer items-center justify-center rounded-full"
            :class="iconClassesMap[theme]"
            @click="emit('add')"
            @keyup.enter.exact="emit('add')"
            @keyup.space.exact="emit('add')"
          >
            <plus-icon class="icon-s" />
          </span>
        </div>
        <chevron-down-icon v-if="showChevron" class="icon-m text-gray-60 ml-auto flex shrink-0 duration-200 ease-out" />
      </component>
    </template>
  </ui-menu-base-trigger>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { ChevronDownIcon, XIcon, PlusIcon } from '../../../icons'
import { UiMenuTriggerTheme } from '../types'
import UiMenuBaseTrigger from './UiMenuBaseTrigger.vue'

withDefaults(
  defineProps<{
    active?: boolean
    disabled?: boolean
    expanded?: boolean
    canRemove?: boolean
    canAdd?: boolean
    theme?: UiMenuTriggerTheme
    size?: keyof typeof sizeClassesMap
    showChevron?: boolean
    dataTestid?: string
    fullWidth?: boolean
    tooltipContent?: string
    error?: boolean
    removeTooltipContent?: string
    disableConditions?: boolean
  }>(),
  {
    theme: UiMenuTriggerTheme.Default,
    size: 'lg',
    showChevron: true,
    dataTestid: 'trigger-content',
    fullWidth: false,
    tooltipContent: '',
    removeTooltipContent: '',
    disableConditions: false,
  },
)

const emit = defineEmits<{
  remove: []
  add: []
}>()

const sizeClassesMap = {
  sm: 'h-6 px-2',
  md: 'h-7 px-2',
  'md-wide': 'h-7 px-3',
  lg: 'h-8 px-3',
}

const themeClassesMap = {
  [UiMenuTriggerTheme.White]: 'hover:bg-gray-10 focus:bg-gray-10 bg-white text-gray-70',
  [UiMenuTriggerTheme.WhiteBordered]: 'hover:bg-gray-10 focus:bg-gray-10 bg-white border border-gray-30 text-gray-70',
  [UiMenuTriggerTheme.Light]:
    'hover:bg-gray-10 focus:bg-gray-10 bg-gray-10 border border-gray-30 focus:border-active-primary focus:shadow-input-focus text-gray-70',
  [UiMenuTriggerTheme.Transparent]: 'hover:bg-gray-20 focus:bg-gray-30 text-gray-70',
  [UiMenuTriggerTheme.Default]: 'bg-gray-20 hover:bg-gray-30 focus:bg-gray-30 text-gray-70',
  [UiMenuTriggerTheme.Blue]: 'bg-gray-20 hover:bg-gray-30 focus:bg-gray-30 text-gray-70',
  [UiMenuTriggerTheme.BlueMultiButton]: 'bg-gray-20 hover:bg-gray-30 focus:bg-gray-30 text-gray-70 !cursor-default',
}

const activeClassesMap = {
  [UiMenuTriggerTheme.White]: 'hover:bg-gray-10 focus:bg-gray-10 bg-white text-gray-70',
  [UiMenuTriggerTheme.WhiteBordered]: 'hover:bg-gray-10 focus:bg-gray-10 bg-white border border-gray-30 text-gray-70',
  [UiMenuTriggerTheme.Light]:
    'hover:bg-gray-10 focus:bg-gray-10 bg-gray-10 border border-gray-30 focus:border-active-primary focus:shadow-input-focus text-gray-70',
  [UiMenuTriggerTheme.Transparent]: 'hover:bg-gray-20 focus:bg-gray-30 text-gray-70',
  [UiMenuTriggerTheme.Default]: 'bg-gray-20 hover:bg-gray-30 focus:bg-gray-30 text-gray-70',
  [UiMenuTriggerTheme.Blue]:
    'bg-active-secondary hover:bg-active-secondary focus:bg-active-secondary text-active-primary',
  [UiMenuTriggerTheme.BlueMultiButton]:
    'bg-active-secondary hover:bg-active-secondary focus:bg-active-secondary text-active-primary !cursor-default',
}

const buttonExpandedColorClassesMap = {
  [UiMenuTriggerTheme.White]: 'aria-expanded:!bg-active-secondary',
  [UiMenuTriggerTheme.WhiteBordered]: 'aria-expanded:!bg-gray-10',
  [UiMenuTriggerTheme.Light]: 'aria-expanded:!bg-gray-10',
  [UiMenuTriggerTheme.Transparent]: 'aria-expanded:!bg-gray-10',
  [UiMenuTriggerTheme.Default]: 'aria-expanded:!bg-gray-10',
  [UiMenuTriggerTheme.Blue]: 'aria-expanded:!bg-gray-10',
  [UiMenuTriggerTheme.BlueMultiButton]: 'aria-expanded:!bg-gray-10',
}

const iconClassesMap = {
  [UiMenuTriggerTheme.White]: 'bg-white hover:bg-gray-10 focus:bg-gray-10 text-gray-60',
  [UiMenuTriggerTheme.WhiteBordered]: 'bg-white hover:bg-gray-10 focus:bg-gray-10 text-gray-60',
  [UiMenuTriggerTheme.Light]: 'bg-white hover:bg-gray-10 focus:bg-gray-10 text-gray-60',
  [UiMenuTriggerTheme.Transparent]: 'bg-white hover:bg-gray-10 focus:bg-gray-10 text-gray-60',
  [UiMenuTriggerTheme.Default]: 'bg-white hover:bg-gray-10 focus:bg-gray-10 text-gray-60',
  [UiMenuTriggerTheme.Blue]: 'hover:bg-gray-10 focus:bg-gray-10 text-gray-60',
  [UiMenuTriggerTheme.BlueMultiButton]: 'hover:bg-white hover:text-zblue-60 focus:bg-gray-10 text-gray-60',
}

const disabledSlotsMap = {
  [UiMenuTriggerTheme.BlueMultiButton]: 'px-1 h-6 -m-1 text-gray-80',
}

const hoverableSlotsMap = {
  [UiMenuTriggerTheme.BlueMultiButton]:
    'hover:text-active-primary hover:bg-gray-20 hover:rounded-sm px-1 h-6 -m-1 text-gray-80',
}

const activeHoverableSlotsMap = {
  [UiMenuTriggerTheme.BlueMultiButton]: 'hover:bg-active-secondary-hover hover:rounded-sm px-1 h-6 -m-1',
}

const labelSlotMap = {
  [UiMenuTriggerTheme.BlueMultiButton]: 'text-gray-70',
}

const buttonRef = ref<HTMLButtonElement | null>(null)

const handleFocus = () => {
  buttonRef.value?.focus()
}
</script>
